import { useRef, useEffect, useState, useMemo } from "react";
import styles from "./ModalVideo.module.css";
import playPause from "../../images/svg/play.svg";

const isRutubeEmbedUrl = (val) =>
  typeof val === "string" &&
  /https?:\/\/(www\.)?rutube\.ru\/play\/embed\/[a-z0-9]+/i.test(val);

const isIframeHtml = (val) =>
  typeof val === "string" && /^\s*<iframe[\s>]/i.test(val);

// Очень простой парсер src из html-строки iframe
const extractIframeSrc = (html) => {
  const m = String(html).match(/src=["']([^"']+)["']/i);
  return m ? m[1] : null;
};

const ModalVideo = ({ src }) => {
  const videoRef = useRef();
  const iframeRef = useRef();
  const progressRef = useRef();
  const knobRef = useRef();

  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);

  // Определяем режим работы
  const mode = useMemo(() => {
    if (isIframeHtml(src)) return "rutube_iframe_html";
    if (isRutubeEmbedUrl(src)) return "rutube_iframe_url";
    return "videoRelatively";
  }, [src]);

  const rutubeEmbedSrc = useMemo(() => {
    if (mode === "rutube_iframe_html") return extractIframeSrc(src);
    if (mode === "rutube_iframe_url") return src;
    return null;
  }, [mode, src]);

  // --- Общие ресеты при смене src ---
  useEffect(() => {
    setLoaded(false);
    setError(false);
    setPreviewMode(true);
    setProgress(0);
    setPaused(true);
  }, [src]);

  // --- Только для HTML5 <video> ---
  useEffect(() => {
    if (mode !== "videoRelatively") return;

    const video = videoRef.current;
    if (!video) return;

    const update = () => {
      if (!video.duration || isDragging) return;
      setProgress((video.currentTime / video.duration) * 100);
    };
    const onPause = () => setPaused(true);
    const onPlay = () => setPaused(false);

    video.addEventListener("timeupdate", update);
    video.addEventListener("pause", onPause);
    video.addEventListener("play", onPlay);

    return () => {
      video.removeEventListener("timeupdate", update);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("play", onPlay);
    };
  }, [mode, isDragging]);

  // Клавиатура — только для <video>
  useEffect(() => {
    if (mode !== "videoRelatively") return;

    const video = videoRef.current;
    if (!video) return;

    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.code === "ArrowRight") video.currentTime += 5;
      if (e.code === "ArrowLeft") video.currentTime -= 5;
      if (e.code === "Space") {
        e.preventDefault();
        video.paused ? video.play() : video.pause();
      }
      if (e.code === "Escape") {
        video.blur();
        // Не закрываем модалку - это делает ModalRoot
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [mode]);

  // Пауза при сворачивании вкладки — только для <video>
  useEffect(() => {
    if (mode !== "videoRelatively") return;

    const video = videoRef.current;
    if (!video) return;

    const onVisibilityChange = () => {
      if (document.hidden && !video.paused) video.pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [mode]);

  // Служебный «превью-кадр» на 1 секунде — только для <video>
  useEffect(() => {
    if (mode !== "videoRelatively") return;

    const video = videoRef.current;
    if (!video) return;

    const onLoadedData = () => {
      video
        .play()
        .then(() => {
          setTimeout(() => {
            video.pause();
            video.currentTime = 1;
            const onSeeked = () => {
              setLoaded(true);
              video.removeEventListener("seeked", onSeeked);
            };
            video.addEventListener("seeked", onSeeked);
          }, 10);
        })
        .catch(() => {
          setLoaded(true);
        });
    };
    video.addEventListener("loadeddata", onLoadedData);
    return () => video.removeEventListener("loadeddata", onLoadedData);
  }, [mode, src]);

  // --- Вспомогательные функции только для <video> ---
  const seek = (percent) => {
    if (mode !== "videoRelatively") return;
    const video = videoRef.current;
    if (video && video.duration) video.currentTime = percent * video.duration;
  };

  const onMouseDownKnob = () => {
    if (mode !== "videoRelatively") return;
    setIsDragging(true);
    const move = (evt) => {
      const container = progressRef.current;
      const rect = container.getBoundingClientRect();
      const offsetX = Math.max(0, Math.min(evt.clientX - rect.left, rect.width));
      const percent = offsetX / rect.width;
      setProgress(percent * 100);
    };
    const up = (evt) => {
      const container = progressRef.current;
      const rect = container.getBoundingClientRect();
      const offsetX = Math.max(0, Math.min(evt.clientX - rect.left, rect.width));
      const percent = offsetX / rect.width;
      seek(percent);
      setIsDragging(false);
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const onClickProgress = (e) => {
    if (mode !== "videoRelatively") return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    seek(percent);
  };

  const handlePlay = () => {
    if (mode !== "videoRelatively") return;
    const video = videoRef.current;
    if (!video) return;

    if (previewMode && video.currentTime >= 0.99 && video.currentTime <= 1.01) {
      video.currentTime = 0;
      setPreviewMode(false);
      const onSeeked = () => {
        video.play();
        video.removeEventListener("seeked", onSeeked);
      };
      video.addEventListener("seeked", onSeeked);
    } else {
      video.play();
    }
  };

  // --- Для <iframe> RuTube: считаем loaded по onLoad, ошибок, увы, не поймать нормально ---
  const handleIframeLoad = () => setLoaded(true);

  return (
    <div className={styles.videoWrapper}>
      {/* Skeleton */}
      {!loaded && !error && (
        <div className={styles.skeleton}>
          <div className={styles.skeletonGradient} />
        </div>
      )}

      {error && (
        <div className={`${styles.skeleton} ${styles.error}`}>
          Ошибка загрузки видео
        </div>
      )}

      {/* Ветка HTML5 <video> */}
      {mode === "videoRelatively" && (
        <>
          <video
            ref={videoRef}
            src={src}
            playsInline
            className={styles.video}
            onClick={() => {
              if (videoRef.current.paused) handlePlay();
              else videoRef.current.pause();
            }}
            onError={() => setError(true)}
            style={{ visibility: loaded && !error ? "visible" : "hidden" }}
          />
          <div
            className={styles.videoIconOverlay}
            style={{ display: loaded && paused && !error ? "flex" : "none" }}
            onClick={() => {
              if (videoRef.current.paused) handlePlay();
              else videoRef.current.pause();
            }}
          >
            <img src={playPause} alt="Play/Pause Icon" decoding="async" loading="lazy" />
          </div>

          <div
            className={styles.progressContainer}
            ref={progressRef}
            onClick={onClickProgress}
          >
            <div className={styles.progressBar} style={{ width: `${progress}%` }} />
            <div
              className={styles.progressKnob}
              style={{ left: `${progress}%` }}
              ref={knobRef}
              onMouseDown={onMouseDownKnob}
            />
          </div>
        </>
      )}

      {/* Ветка RuTube <iframe> */}
      {(mode === "rutube_iframe_url" || mode === "rutube_iframe_html") && rutubeEmbedSrc && (
        <div
          className={`${styles.video} ${styles.rutubeWrapper}`}
          style={{
            visibility: loaded && !error ? "visible" : "hidden",
          }}
        >
          <iframe
            ref={iframeRef}
            src={rutubeEmbedSrc}
            title="RuTube player"
            allow="autoplay; clipboard-write; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            frameBorder="0"
            className={styles.rutubeIframe}
            onLoad={handleIframeLoad}
          />
        </div>
      )}
    </div>
  );
};

export default ModalVideo;
